import type { components } from "@contracts/generated/editor-service";

import {
  EVENT_VISIBILITIES,
  Unreadable,
  readBody,
  type ContentBody,
  type EventVisibility,
  type PromptCall,
} from "./previewPromptCall";

/**
 * Preview Insights — the prompt's ordered provenance layers (#1101).
 *
 * `GET …/preview-runs/{ref}/prompt-calls/{eventRef}/provenance` answers with
 * the `prompt_provenance` event liv-brain#419 recorded for one call, plus one
 * server-resolved destination per segment. This module reads that answer into
 * the shape the layer view renders, and it holds three rules:
 *
 * ## The browser does not compose anything
 *
 * The layers' order is the server's, their attribution is the producer's, and
 * the prompt itself is the CALL's own `systemPrompt` — never a join of these
 * segments. Brain's own invariant is that the visible segments, concatenated,
 * reproduce that prompt byte for byte, and a composition that does not is
 * discarded at source rather than published; re-performing that join here
 * would be a second opinion about a prompt that has already been sent, and a
 * join that happened to differ would be shown to a clinician as the prompt Liv
 * used. So {@link compositionAgreement} VERIFIES the tie the producer states
 * (`compositionDigest` against the prompt body's own digest) and never
 * reconstructs it, and nothing in this file concatenates a segment to another.
 *
 * ## A route is built from a closed slug and this page's own Project
 *
 * {@link sourceHref} takes a `SectionSlug` — one of seven values the generated
 * contract enumerates — and the Project id the run page is already mounted
 * under. No part of a producer coordinate reaches an address: it cannot name
 * another Project, cannot select another candidate, and cannot spell a
 * filesystem path or an internal URL, because it is not in the address at all.
 * The Editor Service refuses a coordinate that is not publishable before it
 * ever reaches this browser; this module additionally refuses to build a route
 * from anything but the slug, which is the property that holds even if that
 * gate is wrong.
 *
 * ## Authorship is stated, never implied
 *
 * {@link ORIGIN_AUTHORSHIP} says who wrote each layer in the clinician's own
 * terms, and the five origins are kept apart: inherited base content, content
 * this candidate authored, text the runtime composed, clinical memory, and the
 * synthetic patient's own words. A UI that showed "source" beside all five
 * would tell a clinician they wrote the runtime's instructions.
 *
 * Pure and hermetic on purpose: it is the part of the feature the fast PR gate
 * (`src/lib`) proves on every push.
 */

export type PreviewRunPromptProvenanceResponse =
  components["schemas"]["PreviewRunPromptProvenance"];
export type PreviewPromptSource = components["schemas"]["PreviewPromptSource"];
export type PreviewPromptSourceDestination =
  components["schemas"]["PreviewPromptSourceDestination"];
export type SectionSlug = components["schemas"]["SectionSlug"];

/**
 * Brain's five segment origins (`clinical-preview-trace.v1`, `$defs.segment`).
 * CLOSED: an origin outside this list is not a new layer to invent a sentence
 * for, it is an account of a prompt this build cannot read — and reading it
 * anyway would mean showing a clinician a layer whose authorship is unknown
 * beside four whose authorship is stated.
 */
export const SEGMENT_ORIGINS = [
  "base_inherited",
  "candidate_authored",
  "runtime_instruction",
  "clinical_memory",
  "synthetic_patient_context",
] as const;
export type SegmentOrigin = (typeof SEGMENT_ORIGINS)[number];

/** The four destinations the Editor Service resolves a coordinate onto. */
export const SOURCE_DESTINATIONS = [
  "section",
  "absent",
  "unsectioned",
  "unplaceable",
] as const satisfies readonly PreviewPromptSourceDestination[];

/**
 * The clinical-configuration sections a source link may address, spelled from
 * the generated contract's own union so the two cannot drift. This is the
 * whole vocabulary an href is built from.
 */
export const SECTION_SLUGS = [
  "bg-sets",
  "chief-complaint",
  "pathways",
  "rules",
  "safety",
  "alerts",
  "summary",
] as const satisfies readonly SectionSlug[];

export const SECTION_LABELS = {
  "bg-sets": "BG sets",
  "chief-complaint": "Main concern",
  pathways: "Pathways",
  rules: "Rules",
  safety: "Safety",
  alerts: "Alerts",
  summary: "Clinical summary content",
} as const satisfies Record<SectionSlug, string>;

/** Where a clinician can open the document a layer was authored in. */
export type LayerSource =
  /**
   * Carries the SLUG, never a built address. The href needs this page's own
   * Project id, which a pure reader has no business holding — see
   * {@link sourceHref}, the one place a route is composed.
   */
  | { kind: "section"; section: SectionSlug; coordinate: string }
  | { kind: "absent" }
  | { kind: "unsectioned"; coordinate: string }
  | { kind: "unplaceable"; coordinate: string }
  /**
   * The server said nothing about this segment — an older service, or a
   * response whose `sources` list is shorter than its segment list. Claimed as
   * its own state rather than folded into `absent`: "this layer names no
   * document" is a fact about the prompt, and "nobody told this browser" is a
   * fact about the read.
   */
  | { kind: "unresolved" };

/**
 * The stable facts a layer is recognised by across two reads of one event.
 *
 * The compact record and the detail read are two answers to two requests, and
 * the second is only allowed to supply BYTES for the first. Everything a
 * clinician reads a layer by — its place, its attribution, the document it
 * names and the digest of its content — has to be the same in both, or the
 * bytes are not this layer's and must not be shown under its heading.
 */
export type LayerIdentity = {
  /** The producer's own `index` member, as reported. */
  index: number | null;
  origin: SegmentOrigin;
  /** The producer's own coordinate string, untouched by the destination reading. */
  coordinate: string | null;
  contentHash: string | null;
  visibility: EventVisibility | null;
};

/** One ordered layer of an effective prompt. */
export type PromptLayer = {
  /** The producer's own `index`, republished — see {@link readPromptProvenance}. */
  index: number | null;
  /** Where the layer sits in the order the prompt was composed in, 0-based. */
  position: number;
  origin: SegmentOrigin;
  label: string | null;
  content: ContentBody;
  source: LayerSource;
  /** What a detail read must agree with before its bytes may be shown here. */
  identity: LayerIdentity;
};

export type PromptProvenance = {
  eventRef: string;
  sequence: number | null;
  occurredAt: string | null;
  visibility: EventVisibility;
  label: string | null;
  /** The call these layers explain, as the producer recorded it. */
  promptEventRef: string | null;
  /** The producer's digest of what the layers compose. Null when it recorded none. */
  compositionDigest: string | null;
  layers: readonly PromptLayer[];
};

export type PromptProvenanceReading =
  /** The run holds nothing readable at all; the run header says why. */
  | { kind: "content_unavailable" }
  /** The run holds the call and no account of how its prompt was composed. */
  | { kind: "not_recorded" }
  /**
   * `at` is a CONTENT-FREE member path such as
   * `provenance.event.detail.segments[2].content.state` — never the value
   * found there, which could be a body.
   */
  | { kind: "unreadable"; at: string }
  | { kind: "readable"; provenance: PromptProvenance };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function integerOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

function memberOf<T extends string>(
  list: readonly T[],
  value: unknown,
): value is T {
  return (
    typeof value === "string" && (list as readonly string[]).includes(value)
  );
}

/**
 * The server's destination for one segment, by POSITION.
 *
 * Matched on the position in `sources`, never on a `source.index` member and
 * never on the segment's own `index`: the producer's R16 makes a segment's
 * index its array position and the service publishes `sources` in segment
 * order, so position is the one key both sides agree on by construction. A
 * consumer keying on a reported index would point a clinician at another
 * layer's document the moment a producer defect made the two disagree — which
 * is the single worst thing this surface can do.
 */
function readSource(value: unknown, at: string): LayerSource {
  if (value === undefined) {
    return { kind: "unresolved" };
  }
  if (!isRecord(value)) {
    throw new Unreadable(at);
  }
  const destination = value["destination"];
  if (!memberOf(SOURCE_DESTINATIONS, destination)) {
    throw new Unreadable(`${at}.destination`);
  }
  if (destination === "absent") {
    return { kind: "absent" };
  }
  const coordinate = stringOrNull(value["sourceCoordinate"]);
  if (coordinate === null || coordinate.length === 0) {
    // Every destination but `absent` names a document, so a missing coordinate
    // beside one is a response this build cannot place — not a link to build
    // without one.
    throw new Unreadable(`${at}.sourceCoordinate`);
  }
  if (destination !== "section") {
    return { kind: destination, coordinate };
  }
  const section = value["section"];
  if (!memberOf(SECTION_SLUGS, section)) {
    // A section slug this build does not render is not a route it may guess
    // at: an href built from an unknown slug is an address that 404s, and one
    // built from anything else is the class of address this leaf forbids.
    throw new Unreadable(`${at}.section`);
  }
  return { kind: "section", section, coordinate };
}

/**
 * The address of the clinical-configuration section a layer is authored in.
 *
 * COMPOSED FROM TWO CLOSED THINGS: the `section` slug, which is a member of
 * {@link SECTION_SLUGS}, and the Project id this run page is already mounted
 * under — the same value every other link on the page is built from. The
 * producer's coordinate is not an input, so a source link cannot escape this
 * Project, cannot select another candidate, and cannot become a filesystem
 * path or an internal URL however the coordinate is spelled.
 */
export function sourceHref(projectId: string, section: SectionSlug): string {
  return `/projects/${encodeURIComponent(projectId)}/config/${section}`;
}

/**
 * Reads one provenance response into the layer view's shape, or says at which
 * member it could not.
 *
 * Tolerant about MEMBERS, strict about MEANING — the posture every reader on
 * this lane takes. The bodies are read by {@link readBody}, the same function
 * the exact-prompt panel uses, so a `restricted_technical` segment that
 * nevertheless carries text, and a withheld one that still names a reference
 * to fetch, are refused here exactly as they are there. A second copy of that
 * policy is what must not exist.
 *
 * The whole account is refused rather than partially rendered. Half a
 * composition reads as a whole one: a clinician shown four of five layers has
 * been told a false thing about how the prompt was built, and told it in the
 * form of a true statement.
 */
export function readPromptProvenance(
  response: PreviewRunPromptProvenanceResponse,
): PromptProvenanceReading {
  try {
    if (!response.run.contentAvailable) {
      return { kind: "content_unavailable" };
    }
    const provenance = response.provenance ?? null;
    if (provenance === null) {
      // Content promised with no slot beside it: a contract disagreement this
      // build fails closed on rather than rendering as a call with no layers.
      throw new Unreadable("provenance");
    }
    if (provenance.state === "not_recorded") {
      return { kind: "not_recorded" };
    }
    const event: unknown = provenance.event ?? null;
    if (!isRecord(event)) {
      throw new Unreadable("provenance.event");
    }
    if (event["kind"] !== "prompt_provenance") {
      throw new Unreadable("provenance.event.kind");
    }
    const eventRef = event["eventRef"];
    if (typeof eventRef !== "string" || eventRef.length === 0) {
      throw new Unreadable("provenance.event.eventRef");
    }
    const visibility = event["visibility"];
    if (!memberOf(EVENT_VISIBILITIES, visibility)) {
      throw new Unreadable("provenance.event.visibility");
    }
    const detail = event["detail"];
    if (!isRecord(detail)) {
      throw new Unreadable("provenance.event.detail");
    }
    const segments = detail["segments"];
    if (!Array.isArray(segments) || segments.length === 0) {
      throw new Unreadable("provenance.event.detail.segments");
    }
    // TOLERANT ABOUT THE MEMBER, the posture every reader on this lane takes.
    // The contract makes it required, but a response that omits it — a rolled
    // back service, a proxy that dropped it — must not take the panel down.
    // An absent list claims no destination for any layer, which is what
    // `unresolved` says, and the layers themselves still render.
    const sources = provenance.sources ?? [];
    const layers = segments.map((segment, position) =>
      readLayer(segment, sources[position], position, visibility),
    );
    const sequence = integerOrNull(event["sequence"]);
    return {
      kind: "readable",
      provenance: {
        eventRef,
        sequence: sequence !== null && sequence >= 1 ? sequence : null,
        occurredAt: stringOrNull(event["occurredAt"]),
        visibility,
        label: stringOrNull(event["label"]),
        promptEventRef: stringOrNull(detail["promptEventRef"]),
        compositionDigest: stringOrNull(detail["compositionDigest"]),
        layers,
      },
    };
  } catch (error) {
    if (error instanceof Unreadable) {
      return { kind: "unreadable", at: error.at };
    }
    throw error;
  }
}

export type PromptProvenanceDetailReading =
  | { kind: "content_unavailable" }
  | { kind: "unreadable"; at: string }
  /** The service answered a different event than the one addressed. Fail closed. */
  | { kind: "mismatch" }
  /**
   * The layers as the detail read carries them — the same layers, with their
   * bytes. The layer LIST is not re-read from this response: its order,
   * attribution and destinations are the ones already on screen, and a detail
   * read that described a different composition is a `mismatch` rather than a
   * silent replacement of what the clinician is looking at.
   */
  | { kind: "readable"; bodies: readonly ContentBody[] };

/**
 * Whether a detail read's layer is the SAME layer as the one already on screen.
 *
 * Compared on every stable fact, not on the count (#1101 review, finding 3).
 * A response with the right number of segments, reordered or replaced, passed
 * a count check and its bodies were then indexed under the compact view's
 * origins and source links — one layer's bytes under another layer's
 * attribution, which is the single worst thing this surface can do.
 *
 * `contentHash` is compared ONLY when both sides state one. R11 makes a
 * non-omitted body carry a digest, so in practice both do; an omitted body may
 * legitimately carry none, and a reading that demanded one would refuse a
 * withheld layer for being withheld. One side stating a digest while the other
 * does not is still a disagreement, and is refused.
 */
function sameLayer(here: LayerIdentity, there: LayerIdentity): boolean {
  return (
    here.index === there.index &&
    here.origin === there.origin &&
    here.coordinate === there.coordinate &&
    here.visibility === there.visibility &&
    here.contentHash === there.contentHash
  );
}

/**
 * The layers' own text, from the ONE read that carries a prompt body
 * (`GET …/events/{eventRef}`, the approved design's §7.4), addressed by the
 * provenance event's own ref.
 *
 * Checked against what is already on screen before a byte is shown: the event
 * must be the one addressed, it must be a `prompt_provenance` event, it must
 * carry the same visibility class, and EVERY layer must match the one it would
 * be rendered under on its index, origin, coordinate, visibility and digest.
 * Nothing about the layer LIST is re-read from this response — its order, its
 * attribution and its destinations are the ones the clinician is already
 * looking at, and a detail read describing a different composition is a
 * `mismatch` rather than a silent replacement of them.
 */
export function readPromptProvenanceDetail(
  response: components["schemas"]["PreviewRunEventDetail"],
  expected: {
    eventRef: string;
    visibility: EventVisibility;
    layers: readonly LayerIdentity[];
  },
): PromptProvenanceDetailReading {
  try {
    if (!response.run.contentAvailable) {
      return { kind: "content_unavailable" };
    }
    const event: unknown = response.event ?? null;
    if (!isRecord(event)) {
      throw new Unreadable("event");
    }
    if (event["kind"] !== "prompt_provenance") {
      throw new Unreadable("event.kind");
    }
    if (event["eventRef"] !== expected.eventRef) {
      return { kind: "mismatch" };
    }
    const visibility = event["visibility"];
    if (!memberOf(EVENT_VISIBILITIES, visibility)) {
      throw new Unreadable("event.visibility");
    }
    // A detail read that DOWNGRADES the class is not a detail of what is on
    // screen: R8's promise about the compact record would be carried into a
    // render of bodies the compact record said were withheld.
    if (visibility !== expected.visibility) {
      return { kind: "mismatch" };
    }
    const detail = event["detail"];
    if (!isRecord(detail)) {
      throw new Unreadable("event.detail");
    }
    const segments = detail["segments"];
    if (!Array.isArray(segments)) {
      throw new Unreadable("event.detail.segments");
    }
    if (segments.length !== expected.layers.length) {
      return { kind: "mismatch" };
    }
    const bodies: ContentBody[] = [];
    for (const [position, segment] of segments.entries()) {
      const at = `event.detail.segments[${position}]`;
      const body = readSegmentBody(segment, at, visibility);
      const origin = isRecord(segment) ? segment["origin"] : null;
      if (!memberOf(SEGMENT_ORIGINS, origin)) {
        throw new Unreadable(`${at}.origin`);
      }
      const identity: LayerIdentity = {
        index: integerOrNull(isRecord(segment) ? segment["index"] : null),
        origin,
        coordinate: stringOrNull(
          isRecord(segment) ? segment["sourceCoordinate"] : null,
        ),
        contentHash: body.contentHash,
        visibility: body.visibility,
      };
      if (!sameLayer(expected.layers[position]!, identity)) {
        return { kind: "mismatch" };
      }
      bodies.push(body);
    }
    return { kind: "readable", bodies };
  } catch (error) {
    if (error instanceof Unreadable) {
      return { kind: "unreadable", at: error.at };
    }
    throw error;
  }
}

function readLayer(
  value: unknown,
  source: unknown,
  position: number,
  eventVisibility: EventVisibility,
): PromptLayer {
  const at = `provenance.event.detail.segments[${position}]`;
  const content = readSegmentBody(value, at, eventVisibility);
  if (!isRecord(value)) {
    throw new Unreadable(at);
  }
  const origin = value["origin"];
  if (!memberOf(SEGMENT_ORIGINS, origin)) {
    throw new Unreadable(`${at}.origin`);
  }
  return {
    index: integerOrNull(value["index"]),
    position,
    origin,
    label: stringOrNull(value["label"]),
    content,
    source: readSource(source, `provenance.sources[${position}]`),
    identity: {
      index: integerOrNull(value["index"]),
      origin,
      // The PRODUCER's own string, not the destination reading's echo of it:
      // this is what two reads of one event are held to agree about.
      coordinate: stringOrNull(value["sourceCoordinate"]),
      contentHash: content.contentHash,
      visibility: content.visibility,
    },
  };
}

/**
 * One layer's body, under the event's own visibility class.
 *
 * R8 IS AN EVENT-SCOPE RULE AND IS ENFORCED AS ONE (#1101 review, finding 2).
 * The producer's `event_prompt_provenance` says that a `restricted_technical`
 * event's detail is the WITHHELD shape — every segment omitted, whatever each
 * segment's own class says. Checking each slot alone left a restricted event
 * carrying a `clinical_full` reference readable, and the detail read would
 * then have rendered that reference's inline text: policy withheld the
 * composition at source and the browser would have shown it anyway.
 *
 * Refused whole rather than filtered. The browser never redacts, and it never
 * shows what policy withheld at source either; a restricted event carrying a
 * readable slot is a defect upstream, not content to render.
 */
function readSegmentBody(
  value: unknown,
  at: string,
  eventVisibility: EventVisibility,
): ContentBody {
  if (!isRecord(value)) {
    throw new Unreadable(at);
  }
  const content = readBody(value["content"], `${at}.content`);
  if (content === null) {
    // Every layer has a body — inline, by reference, or withheld with a
    // reason. A layer with no slot at all is an account this build cannot
    // read, never a layer that contributed nothing.
    throw new Unreadable(`${at}.content`);
  }
  if (
    eventVisibility === "restricted_technical" &&
    content.state !== "omitted"
  ) {
    throw new Unreadable(`${at}.content.state`);
  }
  return content;
}

/** The layer's source with its href filled in for this Project. */
export function layerHref(
  layer: PromptLayer,
  projectId: string,
): string | null {
  return layer.source.kind === "section"
    ? sourceHref(projectId, layer.source.section)
    : null;
}

// ── What each origin means, in the clinician's own terms ─────────────────────

export const ORIGIN_LABELS = {
  base_inherited: "Inherited from the base pack",
  candidate_authored: "Authored in this candidate",
  runtime_instruction: "Composed by the runtime",
  clinical_memory: "Clinical memory",
  synthetic_patient_context: "Synthetic patient context",
} as const satisfies Record<SegmentOrigin, string>;

/**
 * WHO WROTE THIS LAYER, said outright.
 *
 * The Definition of Done's "never claims authored ownership for
 * inherited/runtime content" is a property of these five sentences, not of a
 * badge colour. Each one names an author, and only one of them names the
 * clinician's own candidate.
 */
export const ORIGIN_AUTHORSHIP = {
  base_inherited:
    "Inherited, not authored here. This text comes from the base pack this Project builds on; editing this candidate did not write it.",
  candidate_authored:
    "Authored in this candidate. This text comes from a clinical document this Project supplies.",
  runtime_instruction:
    "Composed by Liv at run time. Nobody authored this text as it stands; where a clinical document shapes it, that document is named below.",
  clinical_memory:
    "Clinical memory carried into this turn. Assembled by Liv from what the conversation had already established, not authored in any document.",
  synthetic_patient_context:
    "The synthetic patient's own context. This is the preview patient's material, not authored clinical content.",
} as const satisfies Record<SegmentOrigin, string>;

export const ORIGIN_TONES = {
  base_inherited: "neutral",
  candidate_authored: "accent",
  runtime_instruction: "outline",
  clinical_memory: "outline",
  synthetic_patient_context: "outline",
} as const satisfies Record<
  SegmentOrigin,
  "outline" | "success" | "neutral" | "warning" | "destructive" | "accent"
>;

/** One provenance chip: an origin, and how many layers of the prompt carry it. */
export type LayerChip = {
  origin: SegmentOrigin;
  count: number;
  /** Where the first layer of this origin sits in the prompt. */
  firstPosition: number;
};

/**
 * The chips, in the order the PROMPT introduces each origin.
 *
 * Not in {@link SEGMENT_ORIGINS} order and not sorted by count: the chips are
 * a reading of the composition, and the composition has an order. A prompt
 * that opens with inherited base rules and ends with the patient's context
 * reads left to right as exactly that.
 */
export function layerChips(layers: readonly PromptLayer[]): LayerChip[] {
  const chips: LayerChip[] = [];
  for (const layer of layers) {
    const existing = chips.find((chip) => chip.origin === layer.origin);
    if (existing === undefined) {
      chips.push({
        origin: layer.origin,
        count: 1,
        firstPosition: layer.position,
      });
    } else {
      existing.count += 1;
    }
  }
  return chips;
}

/** Whether any layer of this prompt was authored in the candidate under test. */
export function hasCandidateAuthoredLayer(
  layers: readonly PromptLayer[],
): boolean {
  return layers.some((layer) => layer.origin === "candidate_authored");
}

// ── The tie between the layers and the prompt they explain ───────────────────

export type CompositionAgreement =
  /** The producer's composition digest is the prompt body's own digest. */
  | { kind: "agrees"; digest: string }
  /** Both digests are stated and they differ. The layers explain another prompt. */
  | { kind: "disagrees"; composition: string; prompt: string }
  /** One side stated no digest, so the tie cannot be checked either way. */
  | { kind: "unstated" }
  /** These layers name another call entirely. */
  | { kind: "other_call"; promptEventRef: string };

/**
 * Whether the layers on screen are the layers OF the prompt on screen.
 *
 * A CHECK, never a reconstruction. Brain's R23 states the tie —
 * `compositionDigest` equals the referenced call's own `systemPrompt` hash —
 * and this compares the two values the producer published. It never joins the
 * segments and never hashes anything: a browser that recomputed the
 * composition would be offering a second opinion about a prompt that has
 * already been sent, and the moment the two opinions differed it would show a
 * clinician the wrong one as the prompt Liv used.
 *
 * `unstated` is a real answer and not a quiet pass. A producer that recorded
 * no digest has told this build nothing about the tie, and a panel that
 * rendered that as agreement would be asserting something nobody stated.
 */
export function compositionAgreement(
  provenance: PromptProvenance,
  call: PromptCall,
): CompositionAgreement {
  if (
    provenance.promptEventRef !== null &&
    provenance.promptEventRef !== call.eventRef
  ) {
    return { kind: "other_call", promptEventRef: provenance.promptEventRef };
  }
  const composition = provenance.compositionDigest;
  const prompt = call.systemPrompt?.contentHash ?? null;
  if (composition === null || prompt === null) {
    return { kind: "unstated" };
  }
  return composition === prompt
    ? { kind: "agrees", digest: composition }
    : { kind: "disagrees", composition, prompt };
}

/** What the agreement means for the clinician reading the panel, in one sentence. */
export function describeAgreement(agreement: CompositionAgreement): string {
  switch (agreement.kind) {
    case "agrees":
      return "These layers are the layers of the prompt above: Liv recorded the same digest for the composition and for the prompt it sent.";
    case "disagrees":
      return "These layers do not match the prompt above. Liv recorded one digest for the composition and a different one for the prompt it sent, so this account of the layers is not an account of that prompt.";
    case "unstated":
      return "Liv recorded no digest for one of the two, so this page cannot confirm that these layers are the layers of the prompt above. Nothing here is reconstructed from the layers.";
    case "other_call":
      return "These layers explain a different model call than the one above. Nothing is shown as that call's composition.";
  }
}

/**
 * The one sentence that says what the layer view is showing.
 *
 * ONE STRING rather than assembled nodes: a screen reader announces a live
 * region as one utterance, and a count split across elements is read as
 * disconnected fragments.
 */
export function describeLayers(layers: readonly PromptLayer[]): string {
  const authored = layers.filter(
    (layer) => layer.origin === "candidate_authored",
  ).length;
  const total = layers.length;
  const parts = `${total} ${total === 1 ? "layer" : "layers"}`;
  if (authored === 0) {
    return `This prompt was composed from ${parts}, in the order shown. None of them was authored in this candidate.`;
  }
  return `This prompt was composed from ${parts}, in the order shown. ${authored} of ${total} ${authored === 1 ? "was" : "were"} authored in this candidate.`;
}

/**
 * What the layer's clinical document is, and where it is opened.
 *
 * TAKES THE ORIGIN, because a coordinate is not a claim of authorship and the
 * sentence must not turn it into one. A `runtime_instruction` layer routinely
 * carries one: the rendered steps block is the runtime's own bytes, and it
 * names the steps document a clinician edits to change it — "origin says how
 * the text got here; coordinate says where to go to change it", in the
 * producer's own words. Saying "Authored in Pathways" beside that layer would
 * tell a clinician they wrote text Liv composed, which is the exact false
 * claim this leaf's Definition of Done forbids, and it is what the first
 * rendering of this panel did.
 */
export function describeSource(
  source: LayerSource,
  origin: SegmentOrigin,
): string {
  switch (source.kind) {
    case "section": {
      const where = SECTION_LABELS[source.section];
      if (origin === "candidate_authored") {
        return `Authored in ${where}.`;
      }
      if (origin === "base_inherited") {
        return `Inherited. The document it came from is shown in ${where}.`;
      }
      return `Not authored as it stands. The clinical document that shapes it is in ${where}, which is where it is changed.`;
    }
    case "absent":
      // ORIGIN-SENSITIVE for the same reason the `section` arm is (#1101
      // review, finding 6): `absent` also covers the synthetic patient's own
      // context and the clinical memory assembled from it, and a blanket "Liv
      // composed it" hands the runtime authorship of the patient's words one
      // line after the panel has said they are the patient's.
      if (origin === "synthetic_patient_context") {
        return "This layer names no clinical document: it is the synthetic patient's own material, not authored content, so there is nothing to open.";
      }
      if (origin === "clinical_memory") {
        return "This layer names no clinical document: Liv assembled it from what the conversation had already established, so there is nothing to open.";
      }
      return "This layer names no clinical document. Liv composed it, so there is nothing to open.";
    case "unsectioned":
      return "This clinical document is not shown in any configuration section of this build, so there is nowhere to open it. Its coordinate is shown instead.";
    case "unplaceable":
      return "This build cannot place this clinical document in the configuration sections it knows. Its coordinate is shown instead.";
    case "unresolved":
      return "The Editor Service reported no destination for this layer, so no source is offered. Nothing is guessed from the coordinate.";
  }
}

// ── The frozen document behind a layer, as THIS run's candidate holds it ─────

export type PreviewRunPromptSourceResponse =
  components["schemas"]["PreviewRunPromptSource"];
export type PreviewPromptSourceState =
  components["schemas"]["PreviewPromptSourceState"];

/**
 * The five states the run-bound source read answers with, spelled from the
 * generated contract so the two cannot drift.
 */
export const SOURCE_STATES = [
  "frozen",
  "not_supplied",
  "no_coordinate",
  "restricted",
  "candidate_unavailable",
] as const satisfies readonly PreviewPromptSourceState[];

export const CONTENT_KINDS = ["yaml", "text", "binary"] as const;
export type FrozenContentKind = (typeof CONTENT_KINDS)[number];

/** One layer's authoring document, exactly as the run's candidate froze it. */
export type FrozenSource = {
  state: PreviewPromptSourceState;
  coordinate: string | null;
  section: SectionSlug | null;
  /** The dotted field address the authoring surface uses, when one exists. */
  address: string | null;
  /** Archive-relative pack path. Non-null only when `state` is `frozen`. */
  path: string | null;
  byteLength: number | null;
  sha256: string | null;
  kind: FrozenContentKind | null;
  /** The frozen bytes, decoded. Null for binary, for an over-cap file, and for every other state. */
  text: string | null;
  /** Which testing version these bytes belong to, as the run page names it. */
  candidateRevision: number | null;
};

export type FrozenSourceReading =
  | { kind: "content_unavailable" }
  | { kind: "unreadable"; at: string }
  /** The second read no longer describes the composition already on screen. */
  | { kind: "mismatch" }
  | { kind: "readable"; source: FrozenSource };

export type FrozenSourceExpectation = {
  /** The compact, body-free composition already on screen. */
  provenance: PromptProvenance;
  /** The ordinal named in the source route. */
  index: number;
};

function nullableString(value: unknown, at: string): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    throw new Unreadable(at);
  }
  return value;
}

function nullableNonNegativeInteger(value: unknown, at: string): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new Unreadable(at);
  }
  return value as number;
}

function sameSource(here: LayerSource, there: LayerSource): boolean {
  if (here.kind !== there.kind) {
    return false;
  }
  if (here.kind === "section" && there.kind === "section") {
    return (
      here.section === there.section && here.coordinate === there.coordinate
    );
  }
  if (
    (here.kind === "unsectioned" || here.kind === "unplaceable") &&
    (there.kind === "unsectioned" || there.kind === "unplaceable")
  ) {
    return here.coordinate === there.coordinate;
  }
  return true;
}

function expectedSection(source: LayerSource): SectionSlug | null {
  return source.kind === "section" ? source.section : null;
}

/**
 * Reads the run-bound source response.
 *
 * NOTHING HERE BUILDS AN ADDRESS, and nothing here asks for a document by
 * name. The browser addressed a layer by its position; the coordinate, the
 * candidate and the archive path were all resolved server-side off the run's
 * own immutable correlation row. `path` and `coordinate` are shown as inert
 * text so a clinician can recognise the document, and neither is ever
 * interpolated into a request or a link.
 */
export function readFrozenSource(
  response: PreviewRunPromptSourceResponse,
  expected: FrozenSourceExpectation,
): FrozenSourceReading {
  try {
    if (!response.run.contentAvailable) {
      return { kind: "content_unavailable" };
    }

    const snapshot = readPromptProvenance({
      run: response.run,
      provenance: response.provenance,
    });
    if (snapshot.kind !== "readable") {
      throw new Unreadable("provenance");
    }
    const here = expected.provenance;
    const there = snapshot.provenance;
    if (
      here.eventRef !== there.eventRef ||
      here.visibility !== there.visibility ||
      here.promptEventRef !== there.promptEventRef ||
      here.compositionDigest !== there.compositionDigest ||
      here.layers.length !== there.layers.length ||
      here.layers.some(
        (layer, index) =>
          !sameLayer(layer.identity, there.layers[index]!.identity) ||
          !sameSource(layer.source, there.layers[index]!.source),
      )
    ) {
      return { kind: "mismatch" };
    }
    const layer = here.layers[expected.index];
    const returnedLayer = there.layers[expected.index];
    if (layer === undefined || returnedLayer === undefined) {
      return { kind: "mismatch" };
    }

    const source: unknown = response.source ?? null;
    if (!isRecord(source)) {
      throw new Unreadable("source");
    }
    const state = source["state"];
    if (!memberOf(SOURCE_STATES, state)) {
      throw new Unreadable("source.state");
    }
    const kind = nullableString(source["kind"], "source.kind");
    if (kind !== null && !memberOf(CONTENT_KINDS, kind)) {
      // A projection class this build has no rendering for is not a file to
      // render as something else.
      throw new Unreadable("source.kind");
    }
    const section = nullableString(source["section"], "source.section");
    if (section !== null && !memberOf(SECTION_SLUGS, section)) {
      throw new Unreadable("source.section");
    }
    const coordinate = nullableString(
      source["sourceCoordinate"],
      "source.sourceCoordinate",
    );
    const address = nullableString(source["address"], "source.address");
    const path = nullableString(source["path"], "source.path");
    const byteLength = nullableNonNegativeInteger(
      source["byteLength"],
      "source.byteLength",
    );
    const sha256 = nullableString(source["sha256"], "source.sha256");
    const body = nullableString(source["text"], "source.text");

    const restricted =
      here.visibility === "restricted_technical" ||
      layer.content.visibility === "restricted_technical" ||
      (layer.content.state === "omitted" &&
        layer.content.reason === "restricted_technical");
    if (
      (restricted && state !== "restricted") ||
      (!restricted && state === "restricted")
    ) {
      return { kind: "mismatch" };
    }
    if (state === "frozen") {
      if (coordinate === null) {
        throw new Unreadable("source.sourceCoordinate");
      }
      if (path === null || path.length === 0) {
        throw new Unreadable("source.path");
      }
      if (byteLength === null) {
        throw new Unreadable("source.byteLength");
      }
      if (sha256 === null || !/^[0-9a-f]{64}$/.test(sha256)) {
        throw new Unreadable("source.sha256");
      }
      if (kind === null) {
        throw new Unreadable("source.kind");
      }
      if (kind === "binary" && body !== null) {
        throw new Unreadable("source.text");
      }
    } else {
      const fileMembers = [address, path, byteLength, sha256, kind, body];
      if (fileMembers.some((member) => member !== null)) {
        throw new Unreadable("source.state");
      }
      if (state === "no_coordinate" || state === "restricted") {
        if (coordinate !== null) {
          throw new Unreadable("source.sourceCoordinate");
        }
        if (section !== null) {
          throw new Unreadable("source.section");
        }
      } else if (coordinate === null) {
        throw new Unreadable("source.sourceCoordinate");
      }
    }

    if (
      (!restricted && coordinate !== layer.identity.coordinate) ||
      (!restricted && section !== expectedSection(layer.source)) ||
      (!restricted &&
        layer.origin === "candidate_authored" &&
        state === "not_supplied") ||
      (!restricted &&
        layer.origin === "base_inherited" &&
        state === "frozen") ||
      (!restricted &&
        (layer.origin === "clinical_memory" ||
          layer.origin === "synthetic_patient_context") &&
        coordinate !== null) ||
      (!restricted &&
        (layer.origin === "base_inherited" ||
          layer.origin === "candidate_authored") &&
        coordinate === null)
    ) {
      return { kind: "mismatch" };
    }

    const dispatch: unknown = response.dispatch;
    if (!isRecord(dispatch)) {
      throw new Unreadable("dispatch");
    }
    const revision: unknown = dispatch["candidateRevision"] ?? null;
    let candidateRevision: number | null = null;
    if (revision !== null) {
      if (!isRecord(revision)) {
        throw new Unreadable("dispatch.candidateRevision");
      }
      candidateRevision = nullableNonNegativeInteger(
        revision["number"],
        "dispatch.candidateRevision.number",
      );
      if (candidateRevision === null) {
        throw new Unreadable("dispatch.candidateRevision.number");
      }
    }
    return {
      kind: "readable",
      source: {
        state,
        coordinate,
        section,
        address,
        path,
        byteLength,
        sha256,
        kind,
        text: body,
        candidateRevision,
      },
    };
  } catch (error) {
    if (error instanceof Unreadable) {
      return { kind: "unreadable", at: error.at };
    }
    throw error;
  }
}

/** What the run's own candidate holds for this layer, as one sentence. */
export function describeFrozenSource(source: FrozenSource): string {
  const version =
    source.candidateRevision === null
      ? "the immutable candidate this run was dispatched with"
      : `Test version ${source.candidateRevision} — the immutable candidate this run was dispatched with`;
  switch (source.state) {
    case "frozen":
      return `These are the exact bytes ${version} holds for this document. They cannot change: a candidate is frozen when it is created, and this page has no way to edit one.`;
    case "not_supplied":
      return `${version} supplies no document at this coordinate. The layer's recorded origin, shown below, says why; archive absence alone is never treated as a claim of inheritance or authorship.`;
    case "no_coordinate":
      return "This layer names no clinical document, so there is nothing frozen to show.";
    case "restricted":
      return "Policy withholds this layer's technical source. No candidate archive was read, and only the safe layer facts recorded above are shown.";
    case "candidate_unavailable":
      return "The candidate this run was dispatched with can no longer be read in this environment, so its frozen bytes cannot be shown. That says nothing about the layer itself.";
  }
}

/** Why a frozen document's text is not on screen, when it is not. */
export function describeFrozenBody(source: FrozenSource): string | null {
  if (source.state !== "frozen" || source.text !== null) {
    return null;
  }
  if (source.kind === "binary") {
    return "This document is not text this page decodes. Its exact length and digest are shown instead.";
  }
  return "This document is too large to carry inline. Its exact length and digest are shown instead, so it is still identified exactly.";
}
